import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { applyActionCode, checkActionCode } from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import { auth } from "../../../config/firebaseConfig";
import { useDispatch } from "react-redux";
import { login } from "../../../redux/features/authSlice";
import dbService from "../../../firebase/dbService";
import Header from "../../landingPage/Header";
import Footer from "../../landingPage/Footer";
import { LuCheckCircle2, LuAlertTriangle, LuLoader2, LuArrowRight, LuLogIn, LuHome } from "react-icons/lu";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");
  const continueUrl = searchParams.get("continueUrl");

  const [status, setStatus] = useState("verifying"); // 'verifying' | 'success' | 'error'
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Prevent double execution in React StrictMode
  const verificationAttemptedRef = useRef(false);

  useEffect(() => {
    if (verificationAttemptedRef.current) return;
    verificationAttemptedRef.current = true;

    const performVerification = async () => {
      // 1. Validate incoming parameters
      if (!oobCode) {
        if (auth.currentUser) {
          try {
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
              setIsLoggedIn(true);
              setVerifiedEmail(auth.currentUser.email || "");
              setStatus("success");
              return; 
            }
          } catch (_) {}
        }
        setStatus("error");
        setErrorMessage("Missing verification code. Please make sure you clicked the full link from your email.");
        return;
      }

      if (mode && mode !== "verifyEmail") {
        // If the action is password reset, redirect to forgotPassword
        if (mode === "resetPassword") {
          navigate(`/login/forgotPassword?oobCode=${oobCode}`, { replace: true });
          return;
        }
        setStatus("error");
        setErrorMessage(`Invalid action mode: ${mode}. Expected email verification.`);
        return;
      }

      try {
        // 2. Check action code validity and extract metadata (such as email)
        let emailFromCode = "";
        try {
          const actionCodeInfo = await checkActionCode(auth, oobCode);
          emailFromCode = actionCodeInfo?.data?.email || "";
          if (emailFromCode) {
            setVerifiedEmail(emailFromCode);
          }
        } catch (checkErr) {
          console.warn("Could not checkActionCode before applying:", checkErr);
        }

        // 3. Apply the verification code
        await applyActionCode(auth, oobCode);

        // 4. Handle session and user profile synchronization
        if (auth.currentUser) {
          setIsLoggedIn(true);
          try {
            await auth.currentUser.reload();
            const user = auth.currentUser;

            // Ensure Firestore metadata documents exist
            const existingUserMeta = await dbService.getdocument("user", user.uid).catch(() => null);
            if (!existingUserMeta) {
              const userMeta = {
                uid: user.uid,
                email: user.email || emailFromCode,
                name: user.displayName || "",
                accountType: "parent",
                createdAt: Timestamp.now(),
              };
              await dbService.createDocumentWithId("user", userMeta, user.uid);

              const roleDocData = {
                uid: user.uid,
                email: user.email || emailFromCode,
                name: user.displayName || "",
                accountType: "parent",
                createdAt: Timestamp.now(),
              };
              await dbService.createDocumentWithId("parents", roleDocData, user.uid);
              sessionStorage.setItem("open_verify_phone_modal_on_load", "true");
            }

            // Sync Redux auth state
            dispatch(login("")).unwrap().catch(() => null);
          } catch (syncErr) {
            console.error("Error syncing profile after email verification:", syncErr);
          }
        }

        setStatus("success");
      } catch (err) {
        console.error("Email verification failed:", err);

        // If code was already applied but user is logged in and email is verified
        if (auth.currentUser?.emailVerified) {
          setIsLoggedIn(true);
          setStatus("success");
          return;
        }

        setStatus("error");
        switch (err.code) {
          case "auth/expired-action-code":
            setErrorMessage("This verification link has expired. Please log in to request a new verification email.");
            break;
          case "auth/invalid-action-code":
            setErrorMessage(
              "This link is invalid or has already been used. If you have already verified your email, you can sign in directly."
            );
            break;
          case "auth/user-disabled":
            setErrorMessage("This account has been disabled. Please contact Practido support.");
            break;
          case "auth/user-not-found":
            setErrorMessage("No user found associated with this verification code.");
            break;
          default:
            setErrorMessage(err.message || "Failed to verify email. Please try again or request a new verification link.");
        }
      }
    };

    performVerification();
  }, [oobCode, mode, navigate, dispatch]);

  const getDestination = () => {
    if (continueUrl) {
      try {
        const parsed = new URL(continueUrl, window.location.origin);
        // If continueUrl points to verify-email itself, default to /dashboard
        if (!parsed.pathname.includes("verify-email")) {
          return parsed.pathname + parsed.search;
        }
      } catch (_) {}
    }
    return "/dashboard";
  };

  // Automatic redirect countdown on verification success
  useEffect(() => {
    if (status !== "success") return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (isLoggedIn) {
            navigate(getDestination(), { replace: true });
          } else {
            const loginQuery = verifiedEmail
              ? `?email=${encodeURIComponent(verifiedEmail)}&verified=true`
              : "?verified=true";
            navigate(`/login/email${loginQuery}`, { replace: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, isLoggedIn, navigate, verifiedEmail]);

  const handleManualRedirect = () => {
    if (isLoggedIn) {
      navigate(getDestination(), { replace: true });
    } else {
      const loginQuery = verifiedEmail
        ? `?email=${encodeURIComponent(verifiedEmail)}&verified=true`
        : "?verified=true";
      navigate(`/login/email${loginQuery}`, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 text-gray-800">
      <Header placement="register" />

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-[0_10px_35px_rgba(0,0,0,0.06)] border border-gray-100 p-8 sm:p-10 text-center transition-all duration-300">
          {/* 1. Loading / Verifying State */}
          {status === "verifying" && (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6 shadow-inner animate-pulse">
                <LuLoader2 className="w-8 h-8 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h2>
              <p className="text-sm text-gray-500 max-w-sm">
                Please wait a moment while we verify your email address with Practido.
              </p>
            </div>
          )}

          {/* 2. Success State */}
          {status === "success" && (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6 shadow-inner">
                <LuCheckCircle2 className="w-10 h-10" />
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Email Verified!</h2>

              {verifiedEmail && (
                <p className="text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-4 py-1.5 mb-3 inline-block">
                  {verifiedEmail}
                </p>
              )}

              <p className="text-sm text-gray-600 mb-6">
                {isLoggedIn
                  ? "Your email address has been verified successfully. Redirecting you to your dashboard..."
                  : "Your email address has been verified successfully! You can now log in to your account."}
              </p>

              <button
                type="button"
                onClick={handleManualRedirect}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 cursor-pointer"
              >
                <span>{isLoggedIn ? "Go to Dashboard" : "Continue to Login"}</span>
                <span className="text-xs opacity-75">({countdown}s)</span>
                <LuArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 3. Error State */}
          {status === "error" && (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-6 shadow-inner">
                <LuAlertTriangle className="w-9 h-9" />
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Verification Link Issue</h2>

              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {errorMessage}
              </p>

              <div className="w-full flex flex-col gap-3">
                <Link
                  to="/login/email"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 no-underline"
                >
                  <LuLogIn className="w-4 h-4" />
                  <span>Go to Login</span>
                </Link>

                <Link
                  to="/"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors focus:outline-none no-underline text-sm"
                >
                  <LuHome className="w-4 h-4" />
                  <span>Back to Home</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VerifyEmail;
